
export default class CustomSelect extends HTMLElement {
  private select: HTMLSelectElement | null = null;
  private wrapper: HTMLElement | null = null;
  private trigger: HTMLElement | null = null;
  private dropdown: HTMLElement | null = null;
  private isOpen: boolean = false;
  private currentlySelectedElement: HTMLElement | null = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.style.display = 'block';
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.init();
    
    // Observe for option changes (since they are loaded async)
    const observer = new MutationObserver(() => {
      this.updateOptions();
    });
    observer.observe(this.select, { childList: true, subtree: true });
  }

  init() {
    if (!this.select) return;
    
    // Hide original select but keep it accessible for form submission if needed
    // and for Alpine x-model binding
    this.select.style.display = 'none';

    // Create Wrapper
    this.wrapper = document.createElement('div');
    this.wrapper.className = 'custom-select-wrapper';
    
    // Create Trigger
    this.trigger = document.createElement('div');
    this.trigger.className = 'custom-select-trigger';
    this.trigger.innerHTML = `
      <span class="custom-select-value">Choisir</span>
      <div class="custom-select-icon">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 7.5L10 12.5L15 7.5" stroke="#072431" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    `;
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggle();
    });

    // Create Dropdown
    this.dropdown = document.createElement('ul');
    this.dropdown.className = 'custom-select-dropdown';
    
    this.wrapper.appendChild(this.trigger);
    this.wrapper.appendChild(this.dropdown);
    this.appendChild(this.wrapper);

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target as Node)) {
        this.close();
      }
    });

    this.updateOptions();
  }

  updateOptions() {
    if (!this.select || !this.dropdown || !this.trigger) return;

    this.dropdown.innerHTML = '';
    const options = Array.from(this.select.options);
    const selectedValueSpan = this.trigger.querySelector('.custom-select-value');

    // Update trigger text based on current selection
    if (this.select.value && this.select.selectedOptions[0]) {
        if (selectedValueSpan) {
            selectedValueSpan.textContent = this.select.selectedOptions[0].text;
            selectedValueSpan.setAttribute('style', `font-family: '${this.select.value}', sans-serif;`);
            selectedValueSpan.classList.add('has-value');
        }
    } else {
        if (selectedValueSpan) {
            // Default placeholder
             const placeholder = options.find(opt => opt.disabled && opt.selected) || options[0];
             selectedValueSpan.textContent = placeholder ? placeholder.text : 'Choisir';
             selectedValueSpan.setAttribute('style', '');
             selectedValueSpan.classList.remove('has-value');
        }
    }

    options.forEach(option => {
      if (option.disabled) return; // Skip placeholder

      const item = document.createElement('li');
      item.className = 'custom-select-option';
      if (this.select && (option.selected || option.value === this.select.value)) {
        item.classList.add('selected');
      }
      item.textContent = option.text;
      // Apply font family to the item
      item.style.fontFamily = `'${option.value}', sans-serif`;
      
      item.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectOption(option.value);
      });

      this.dropdown?.appendChild(item);
    });
  }

  selectOption(value: string) {
    if (!this.select) return;
    
    this.select.value = value;

    // Explicitly update selected attribute for options
    Array.from(this.select.options).forEach(opt => {
        if (opt.value === value) {
            opt.selected = true;
            opt.setAttribute('selected', 'selected');
        } else {
            opt.selected = false;
            opt.removeAttribute('selected');
        }
    });

    // Dispatch events for Alpine/Form handling
    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    this.select.dispatchEvent(new Event('input', { bubbles: true }));
    
    this.updateOptions();
    this.close();
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.dropdown || !this.trigger) return;
    
    // Make it visible but hidden to measure
    this.dropdown.style.visibility = 'hidden';
    this.dropdown.classList.add('is-open');
    
    const EXCEPTED_HEIGHT = 400; // Extra space to consider
    const dropdownHeight = Math.min(this.dropdown.offsetHeight, EXCEPTED_HEIGHT);
    const rect = this.trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    
    // Reset visibility
    this.dropdown.style.visibility = '';

    this.isOpen = true;
    
    // Rotate icon
    const icon = this.trigger.querySelector('.custom-select-icon');
    if (icon) icon.classList.add('rotate-180');

    if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
      // Open upwards
      this.dropdown.style.bottom = '100%';
      this.dropdown.style.top = 'auto';
      this.dropdown.style.marginBottom = '8px';
      this.dropdown.style.marginTop = '0';
    } else {
      // Open downwards
      this.dropdown.style.top = '100%';
      this.dropdown.style.bottom = 'auto';
      this.dropdown.style.marginTop = '8px';
      this.dropdown.style.marginBottom = '0';
    }
  }

  close() {
    if (!this.dropdown || !this.trigger) return;
    
    this.isOpen = false;
    this.dropdown.classList.remove('is-open');
    
    // Reset icon rotation
    const icon = this.trigger.querySelector('.custom-select-icon');
    if (icon) icon.classList.remove('rotate-180');
  }
}
